import { memo, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Region } from 'clickNgo/configs/geo'
import {
  ContactStatus,
  ContactWorkflowStatus,
  IContact,
  LoadingState,
} from 'clickNgo/entities/contact'
import { fetchContactRequestById } from 'clickNgo/entities/contact/api'
import { mapContactModifiedValues } from 'clickNgo/entities/contact/api/mapping/mapContactModifiedValues'
import { mapPartnerFromPartnerAllDataRegions } from 'clickNgo/entities/partner/api/mapping'
import { ContactRelinkingValidationMessage } from 'clickNgo/features/contact/contactRelinking'
import { useAppSource } from 'clickNgo/hooks/utils/useAppSource'
import { I375Form } from 'clickNgo/models/crud/forms'
import { Nullable } from 'clickNgo/models/utils'
import {
  FormDropdown,
  FormInput,
  resetAllModifiedDataByKey,
  resetModifiedDataByField,
  updateInvalidData,
  useGetFormDataField,
} from 'cng-common/features/form'
import { FieldWrapperWithChanges } from 'cng-common/features/form/components'
import { ValidationType } from 'cng-common/features/form/configs'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { IForm } from 'core-components/classes/models/IForm'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { FieldGroup } from 'shared/components/fieldGroup'

import { ContactEditorFormKeys } from '../../../configs'
import { useReadOnlyMenuItem } from '../../../hooks/useReadOnlyMenuItem'
import {
  ContactEditorSettingFormFields,
  TContactEditorData,
  TContactEditorHandles,
} from '../../../models'
import { ContactEditableContainer, IOnApplyProps } from '../../contactEditableContainer'

import { checkCriticalRelinkingPoints } from './helpers/checkCriticalRelinkingPoints'
import { getPartnerIdSource } from './helpers/getPartnerIdSource'
import { isRolesDifferent } from './helpers/isRolesDifferent'
import { performRelink } from './helpers/performRelink'
import { updateRoles } from './helpers/updateRoles'

import styles from './WrappedContactRelinking.module.scss'

type TProps = Pick<TContactEditorHandles, 'onContactUpdate'> &
  Pick<TContactEditorData, 'contact' | 'contactFlags'>
/*
 * TODO: Fix label and fields
 * */
export const WrappedContactRelinking = memo<TProps>(
  ({ contact, contactFlags, onContactUpdate }) => {
    const dispatch = useAppDispatch()
    const source = useAppSource()
    const { isCurrentMenuItemReadOnly } = useReadOnlyMenuItem()
    const [relinkingCriticalMessage, setRelinkingCriticalMessage] = useState<Nullable<string>>(null)
    const [actualData, updateActualData] = useGetFormDataField<TContactEditorData>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.ActualData
    )
    const user = useSelector(selectUser)

    const [errorMessages, setErrorMessages] = useGetFormDataField<string[]>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.ErrorMessages
    )
    const [, setIsLoading] = useGetFormDataField<LoadingState>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.IsLoading
    )

    const partnerIsSource = useMemo(
      () => getPartnerIdSource(contact.partnerId),
      [contact.partnerId]
    )

    const [newPartnerId, , isNewPartnerIdModified] = useGetFormDataField<string>(
      ContactEditorFormKeys.contactRelinking,
      'newPartnerId'
    )

    const isContactRelinking = Boolean(
      contact.newPartnerId && contact.workflowStatus && contact.newPartnerId === newPartnerId
    )
    const [relinkingComment, updateRelinkingComment, isRelinkingCommentModified] =
      useGetFormDataField<string>(ContactEditorFormKeys.contactRelinking, 'relinkingComment')

    const handleContactRelink = useCallback(
      async (newHpRoles?: string[]) => {
        const actualContact = await fetchContactRequestById(contact.contactId)

        if (!actualContact) {
          setErrorMessages(['Fetching actual contact data error'])
          return
        }

        const contactBase: IContact = {
          ...actualContact,
          newPartnerId,
          relinkingComment,
          sourceRequester: user.email,
          workflowStatus: ContactWorkflowStatus.New,
          status: ContactStatus.Active,
          source,
        }

        const relinkResult = await performRelink({
          contact: contactBase,
          isRelinkingCommentModified,
        })

        if (!relinkResult.success) {
          setErrorMessages(relinkResult.errors)
          return
        }

        const isNeedUpdateRoles = !newHpRoles
          ? false
          : isRolesDifferent(contactBase.hpRoles, newHpRoles)

        if (!isNeedUpdateRoles || !newHpRoles) {
          return relinkResult
        }

        const rolesResult = await updateRoles({
          contact: contactBase,
          newHpRoles,
          newPartnerId,
        })
        if (!rolesResult.success) {
          setErrorMessages(rolesResult.errors)
          return
        }
        return rolesResult
      },
      [
        contact,
        isRelinkingCommentModified,
        newPartnerId,
        relinkingComment,
        setErrorMessages,
        source,
        user.email,
      ]
    )

    const onApplyHandler = useCallback(
      async ({ newHpRoles }: IOnApplyProps): Promise<void> => {
        try {
          setIsLoading({
            isLoading: true,
            message: isNewPartnerIdModified ? 'Relinking contact...' : 'Updating contact...',
          })

          const updatedRes = await handleContactRelink(newHpRoles)
          if (updatedRes && updatedRes.responseData) {
            const updatedContact: IContact = {
              ...updatedRes.contact,
              ...updatedRes.responseData,
              modifiedValues: mapContactModifiedValues(
                updatedRes.responseData.modifiedValues.split(',') as (keyof I375Form)[]
              ),
              modified: new Date().toISOString(),
            }

            await onContactUpdate(updatedContact)
            updateActualData({ ...actualData, contact: updatedContact })
          }
        } catch (error) {
          console.error('Error updating contact:', error)
          setErrorMessages(['Unexpected error occurred'])
        } finally {
          setIsLoading({ isLoading: false, message: '' })
        }
      },
      [
        actualData,
        handleContactRelink,
        isNewPartnerIdModified,
        onContactUpdate,
        setErrorMessages,
        setIsLoading,
        updateActualData,
      ]
    )

    const onCancelHandler = useCallback(async () => {
      dispatch(resetAllModifiedDataByKey(ContactEditorFormKeys.contactRelinking))
    }, [dispatch])

    const checkRelinkingStepComment = useCallback(async () => {
      const formValidation = FormValidationBuilder.build(ContactEditorFormKeys.contactRelinking)
      const validationResult = await formValidation.call('relinkingComment', relinkingComment)

      if (validationResult) {
        const { field, message } = validationResult
        setErrorMessages(Array.from(new Set([...errorMessages, `${field}: ${message}`])))
      }
    }, [relinkingComment, setErrorMessages, errorMessages])

    const onChangeRelinkingPartnerId = useCallback(
      async (value: string, item?: IForm) => {
        if (!value) {
          updateRelinkingComment('')
          dispatch(
            updateInvalidData({
              key: ContactEditorFormKeys.contactRelinking,
              field: 'relinkingComment',
              validationMessage: '',
            })
          )
          return
        }

        if (item) {
          const mappedPartner = mapPartnerFromPartnerAllDataRegions(item)
          const criticalMessage = await checkCriticalRelinkingPoints(mappedPartner, contact)
          setRelinkingCriticalMessage(criticalMessage)
        }
        checkRelinkingStepComment()
      },
      [checkRelinkingStepComment, contact, dispatch, updateRelinkingComment]
    )

    const closeCriticalRelinkingModal = useCallback(() => {
      setRelinkingCriticalMessage(null)
      dispatch(
        resetModifiedDataByField({
          formKey: ContactEditorFormKeys.contactRelinking,
          field: 'newPartnerId',
        })
      )
    }, [dispatch])

    return (
      <ContactEditableContainer
        contact={contact}
        contactFlags={contactFlags}
        onApply={onApplyHandler}
        onCancel={onCancelHandler}
        onUpdate={onContactUpdate}
        hasModifiedValues
      >
        <FieldGroup title="Contact info" size={0} classNames={{ groupFields: styles.groupFields }}>
          <FormInput
            formKey={ContactEditorFormKeys.contactRelinking}
            defaultValue={contact.firstName}
            field="firstName"
            label="First Name"
            isReadOnly
          />
          <FormInput
            formKey={ContactEditorFormKeys.contactRelinking}
            defaultValue={contact.lastName}
            field="lastName"
            label="Last Name"
            isReadOnly
          />
          <FormInput
            formKey={ContactEditorFormKeys.contactRelinking}
            defaultValue={contact.email}
            field="email"
            label="Email"
            isReadOnly
          />
          <FormInput
            formKey={ContactEditorFormKeys.contactRelinking}
            defaultValue={contact.partnerId}
            field="currentPartner"
            label="Current Partner"
            isReadOnly
          />
          <FieldWrapperWithChanges hasChanges={Boolean(contact.newPartnerId)}>
            <FormDropdown
              source={partnerIsSource}
              caption="PartnerID"
              formKey={ContactEditorFormKeys.contactRelinking}
              defaultValue={contact.newPartnerId}
              field="newPartnerId"
              label="New Partner"
              isReadOnly={isCurrentMenuItemReadOnly}
              onChange={onChangeRelinkingPartnerId}
              renderFunction={(item) => {
                return (
                  <div>
                    {item.CommunicationName} &lt;{item.PartnerID}&gt;
                  </div>
                )
              }}
              fieldsToSearch={['PartnerID', 'CommunicationName']}
            />
          </FieldWrapperWithChanges>
          <FieldWrapperWithChanges hasChanges={Boolean(contact.relinkingComment)}>
            <FormInput
              formKey={ContactEditorFormKeys.contactRelinking}
              defaultValue={contact.relinkingComment}
              field="relinkingComment"
              label="Comment"
              isDisabled={!newPartnerId || isContactRelinking}
              isReadOnly={isCurrentMenuItemReadOnly}
              isRequired={contact.region === Region.APJ && !isContactRelinking}
              validationType={ValidationType.ONCHANGE}
            />
          </FieldWrapperWithChanges>
        </FieldGroup>
        <ContactRelinkingValidationMessage
          text={relinkingCriticalMessage}
          onClose={closeCriticalRelinkingModal}
        />
      </ContactEditableContainer>
    )
  }
)

WrappedContactRelinking.displayName = 'WrappedContactRelinking'
